import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '../i18n';

/**
 * Custom hook for module-specific translations
 * This hook provides a convenient way to use translations from a specific module namespace
 * while also having access to common translations as a fallback
 * 
 * @param {string} namespace - The namespace to use (e.g., 'campaigns', 'draws', 'users')
 * @returns {object} - Translation function and i18n instance
 * 
 * @example
 * // In a campaign component:
 * const { t } = useModuleTranslation('campaigns');
 * // Use: t('title') instead of t('campaigns:title')
 * 
 * @example
 * // Access common translations:
 * const { t, tCommon } = useModuleTranslation('campaigns');
 * // Use: tCommon('loading') for common translations
 */
export const useModuleTranslation = (namespace) => {
  const { t: translate, i18n } = useTranslation([namespace, NAMESPACES.COMMON]);
  
  // Helper function for common translations
  const tCommon = (key, options) => translate(`${NAMESPACES.COMMON}:${key}`, options);
  
  return {
    t: translate,
    tCommon,
    i18n,
    namespace,
  };
};

/**
 * Pre-configured hooks for each module
 * These can be imported directly in module components
 */
export const useAuthTranslation = () => useModuleTranslation(NAMESPACES.AUTH);
export const useCampaignsTranslation = () => useModuleTranslation(NAMESPACES.CAMPAIGNS);
export const useDrawsTranslation = () => useModuleTranslation(NAMESPACES.DRAWS);
export const useAnnouncementsTranslation = () => useModuleTranslation(NAMESPACES.ANNOUNCEMENTS);
export const useSurveysTranslation = () => useModuleTranslation(NAMESPACES.SURVEYS);
export const useBrandsTranslation = () => useModuleTranslation(NAMESPACES.BRANDS);
export const useTenantsTranslation = () => useModuleTranslation(NAMESPACES.TENANTS);
export const useUsersTranslation = () => useModuleTranslation(NAMESPACES.USERS);
export const usePaymentsTranslation = () => useModuleTranslation(NAMESPACES.PAYMENTS);
export const useDashboardTranslation = () => useModuleTranslation(NAMESPACES.DASHBOARD);
export const useVideosTranslation = () => useModuleTranslation(NAMESPACES.VIDEOS);
export const useTicketsTranslation = () => useModuleTranslation(NAMESPACES.TICKETS);
export const useReportsTranslation = () => useModuleTranslation(NAMESPACES.REPORTS);
export const useCommonTranslation = () => useModuleTranslation(NAMESPACES.COMMON);
export const useErrorsTranslation = () => useModuleTranslation(NAMESPACES.TRANSLATION);

export default useModuleTranslation;
